import React, { useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { Box, Button, Checkbox, Dialog, DialogContent, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { IdeaTextCopyButton } from './campaignImagePromptUI';
import { downloadDataUrl, extFromDataUrl, getSectionSlotIndices, getSlotIdeaNote, getSlotImage, VISUAL_SLOT_SECTIONS } from './campaignVisualSlotUtils';
import {
  ensureCloudMigratedFromLocal,
  getCampaignIdeaListUpdate,
  getCampaignStateRef,
  migrateCampaignListItems,
  patchCampaignState
} from '../../utils/campaignFirestoreSync';
const BRANCH_MAP = {
  kavakli_sube: 'Kavaklı Şube',
  sefakoy_sube: 'Sefaköy Şube',
  merkez_sube: 'Merkez Şube'
};

const safeString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
const safeArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const PAGE_SIZE = 5;
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const parseTrDateStart = (value) => {
  const [day, month, year] = safeString(value).split('.').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const formatCountdown = (ms) => {
  const safeMs = Math.max(0, Number(ms) || 0);
  const totalSeconds = Math.floor(safeMs / MS_PER_SECOND);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const p2 = (n) => String(n).padStart(2, '0');
  return `${p2(days)}g ${p2(hours)}s ${p2(minutes)}d ${p2(seconds)}sn`;
};

const getBranchSlug = (forcedBranch) => {
  if (BRANCH_MAP[forcedBranch]) return forcedBranch;
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('sube');
  return BRANCH_MAP[fromUrl] ? fromUrl : 'kavakli_sube';
};

const loadStatus = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('campaign_staff_task_status_v1') || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
};

const buildCampaignTasks = (campaign) => {
  const storeTasks = safeArray(campaign?.storePlan).map((text, index) => ({
    id: `store-${index}`,
    group: 'Mağaza',
    text
  }));
  const staffTasks = safeArray(campaign?.staffPlan).map((text, index) => ({
    id: `staff-${index}`,
    group: 'Personel',
    text
  }));
  const customTasks = safeArray(campaign?.customIdeas).map((text, index) => ({
    id: `custom-${index}`,
    group: 'Yeni Fikir',
    text
  }));
  const submittedTasks = safeArray(campaign?.submittedIdeas)
    .filter((idea) => idea?.approved)
    .map((idea, index) => ({
      id: `submitted-${safeString(idea?.id, index.toString())}`,
      group: 'Sunulan Fikir',
      text: safeString(idea?.text)
    }));
  return [...storeTasks, ...staffTasks, ...customTasks, ...submittedTasks];
};

const sectionSx = {
  border: '1px solid rgba(151,113,39,0.18)',
  borderRadius: 1.8,
  p: 1.15,
  bgcolor: 'rgba(255,255,255,0.72)'
};

const detailTitleSx = {
  color: '#7a4d0b',
  fontSize: '1rem',
  fontWeight: 900,
  mb: 0.45
};

const detailTextSx = {
  color: '#2f2516',
  fontSize: '0.92rem',
  lineHeight: 1.45
};

const renderDetailList = (items, emptyText) => {
  if (!items.length) {
    return <Typography sx={{ ...detailTextSx, color: '#7a6b55', fontStyle: 'italic' }}>{emptyText}</Typography>;
  }
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.2, ...detailTextSx, '& li': { mb: 0.45 } }}>
      {items.map((text, idx) => (
        <li key={`${text}-${idx}`}>
          <Typography component="span" sx={detailTextSx}>
            {text}
          </Typography>
        </li>
      ))}
    </Box>
  );
};

const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const buildPrintHtmlList = (title, items, emptyText) => {
  let html = `<h2>${escapeHtml(title)}</h2>`;
  if (!items.length) {
    html += `<p class="muted">${escapeHtml(emptyText)}</p>`;
    return html;
  }
  html += '<ol>';
  items.forEach((item) => {
    html += `<li>${escapeHtml(item)}</li>`;
  });
  html += '</ol>';
  return html;
};

function StaffBranchCampaignPanel({ sube, activeCampaign, embedded = false, onStatusChange, onCampaignUpdate }) {
  const branchSlug = getBranchSlug(sube);
  const branchName = BRANCH_MAP[branchSlug];
  const [cloudBundle, setCloudBundle] = useState(null);
  const [embeddedStaff, setEmbeddedStaff] = useState(loadStatus);

  useEffect(() => {
    if (embedded) return;
    let cancelled = false;
    (async () => {
      await ensureCloudMigratedFromLocal();
    })();
    const unsub = onSnapshot(
      getCampaignStateRef(),
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setCloudBundle({ campaignList: [], staffTaskStatus: {} });
          return;
        }
        const d = snap.data();
        setCloudBundle({
          campaignList: migrateCampaignListItems(d.campaignList),
          staffTaskStatus:
            d.staffTaskStatus && typeof d.staffTaskStatus === 'object' && !Array.isArray(d.staffTaskStatus) ? d.staffTaskStatus : {}
        });
      },
      () => {
        if (!cancelled) {
          setCloudBundle({ campaignList: [], staffTaskStatus: {} });
        }
      }
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [embedded]);

  const staffTaskStatus = embedded ? embeddedStaff : cloudBundle?.staffTaskStatus || {};
  const campaign = embedded
    ? activeCampaign || null
    : !cloudBundle
      ? null
      : activeCampaign || cloudBundle.campaignList[0] || null;
  const campaignId = safeString(campaign?.id);
  const [tab, setTab] = useState(0);
  const [taskPage, setTaskPage] = useState(1);
  const [ideaInput, setIdeaInput] = useState('');
  const [ideaSuccess, setIdeaSuccess] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [visualSectionTab, setVisualSectionTab] = useState(0);
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const tasks = useMemo(() => buildCampaignTasks(campaign), [campaign]);
  const totalPages = Math.max(1, Math.ceil(tasks.length / PAGE_SIZE));
  const currentPage = Math.min(taskPage, totalPages);
  const pagedTasks = tasks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const completedCount = tasks.filter((task) => staffTaskStatus?.[branchSlug]?.campaignTasks?.[campaignId]?.[task.id]).length;
  const completionRate = tasks.length ? Math.round((completedCount / tasks.length) * 100) : 0;
  const campaignGoal = safeString(campaign?.campaignDetails?.goal, safeString(campaign?.goal, 'Kampanya hedefi belirtilmedi.'));
  const campaignAiGoal = safeString(
    campaign?.campaignDetails?.aiGoalText,
    `${safeString(campaign?.name, 'Kampanya')} için kampanya hedefi; ${campaignGoal.toLocaleLowerCase(
      'tr-TR'
    )} odağını mağaza, personel ve dijital iletişimde aynı çizgide uygulamak, müşteri ilgisini artırmak ve satış dönüşümünü güçlendirmektir.`
  );
  const selectedStoreIdeas = safeArray(campaign?.storePlan).filter(Boolean);
  const selectedStaffIdeas = safeArray(campaign?.staffPlan).filter(Boolean);
  const selectedWebsiteIdeas = safeArray(campaign?.websitePlan).length
    ? safeArray(campaign?.websitePlan).filter(Boolean)
    : safeArray(campaign?.internetPlan).filter(Boolean);
  const selectedInstagramIdeas = safeArray(campaign?.instagramPlan).filter(Boolean);
  const selectedCustomIdeas = safeArray(campaign?.customIdeas).filter(Boolean);
  const approvedSubmittedIdeas = safeArray(campaign?.submittedIdeas)
    .filter((idea) => idea?.approved)
    .map((idea) => `${safeString(idea?.branchLabel, 'Şube')} — ${safeString(idea?.createdAt, '-')}: ${safeString(idea?.text, '-')}`);
  const campaignStartDate = parseTrDateStart(campaign?.start);
  const ideaDeadlineMs = campaignStartDate ? campaignStartDate.getTime() - MS_PER_DAY : null;
  const remainingIdeaSubmitMs = ideaDeadlineMs == null ? 0 : Math.max(0, ideaDeadlineMs - nowMs);
  const isIdeaSubmitLocked = ideaDeadlineMs == null ? true : nowMs >= ideaDeadlineMs;
  const ideaCountdownText = formatCountdown(remainingIdeaSubmitMs);

  const campaignImageSlug = useMemo(() => {
    const n = safeString(campaign?.name, 'kampanya');
    return n
      .replace(/[^\w\-\sğüşıöçĞÜŞİÖÇ]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 48);
  }, [campaign?.name]);

  const isChecked = (taskId) => !!staffTaskStatus?.[branchSlug]?.campaignTasks?.[campaignId]?.[taskId];

  const toggleTask = (taskId) => {
    if (!campaignId) return;
    const buildNext = (prev) => ({
      ...prev,
      [branchSlug]: {
        ...(prev[branchSlug] || {}),
        campaignTasks: {
          ...(prev[branchSlug]?.campaignTasks || {}),
          [campaignId]: {
            ...(prev[branchSlug]?.campaignTasks?.[campaignId] || {}),
            [taskId]: !prev[branchSlug]?.campaignTasks?.[campaignId]?.[taskId]
          }
        }
      }
    });
    if (embedded) {
      setEmbeddedStaff((prev) => {
        const next = buildNext(prev);
        if (typeof onStatusChange === 'function') onStatusChange(next);
        return next;
      });
    } else {
      const next = buildNext(cloudBundle?.staffTaskStatus || {});
      void patchCampaignState({ staffTaskStatus: next });
    }
  };

  const submitIdea = () => {
    const text = safeString(ideaInput).trim();
    if (!campaignId || !text) return;
    const idea = {
      id: `idea_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      branchSlug,
      branchLabel: branchName,
      text,
      approved: false,
      createdAt: new Date().toLocaleString('tr-TR')
    };
    const updatedCampaign = {
      ...campaign,
      submittedIdeas: [...safeArray(campaign?.submittedIdeas), idea]
    };
    if (embedded) {
      if (typeof onCampaignUpdate === 'function') onCampaignUpdate(updatedCampaign);
    } else {
      const nextList = getCampaignIdeaListUpdate(cloudBundle?.campaignList, campaignId, updatedCampaign);
      void patchCampaignState({ campaignList: nextList });
    }
    setIdeaInput('');
    setIdeaSuccess('Fikriniz yönetici onayına gönderildi.');
  };

  const handlePrintCampaignDetail = () => {
    if (!campaign) return;
    const name = safeString(campaign?.name, 'Kampanya');
    const start = safeString(campaign?.start, '-');
    const end = safeString(campaign?.end, '-');
    const printStoreSection = buildPrintHtmlList('Mağaza / Vitrin / Banko', selectedStoreIdeas, 'Bu kanalda henüz fikir yok.');
    const printStaffSection = buildPrintHtmlList('Personel', selectedStaffIdeas, 'Bu kanalda henüz fikir yok.');
    const printCustomSection = selectedCustomIdeas.length
      ? buildPrintHtmlList('Yeni Fikir', selectedCustomIdeas, '')
      : '';
    const printApprovedSection = approvedSubmittedIdeas.length
      ? buildPrintHtmlList('Şubelerden Onaylanan Fikirler', approvedSubmittedIdeas, '')
      : '';
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(name)} - Kampanya Detayı</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: Inter, system-ui, -apple-system, "Segoe UI", sans-serif;
      margin: 0;
      color: #1f1a14;
      background: #f8f4ea;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page {
      width: 100%;
      max-width: 1000px;
      margin: 0 auto;
      padding: 22px 18px 20px;
    }
    .hero {
      background: linear-gradient(130deg, #fffaf0 0%, #f0e3cd 100%);
      border: 1px solid rgba(151,113,39,0.35);
      border-radius: 12px;
      padding: 16px 18px;
      margin-bottom: 14px;
    }
    .hero h1 {
      font-size: 1.48rem;
      margin: 0 0 7px;
      letter-spacing: 0.01em;
      color: #2f2516;
    }
    .meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin: 0;
    }
    .chip {
      border: 1px solid rgba(151,113,39,0.3);
      border-radius: 999px;
      padding: 4px 10px;
      font-size: 0.82rem;
      font-weight: 700;
      color: #5c4d3a;
      background: rgba(255,255,255,0.75);
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
      margin-bottom: 10px;
    }
    .card {
      border: 1px solid rgba(151,113,39,0.26);
      border-radius: 10px;
      padding: 10px 12px;
      background: #fff;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .card h2 {
      margin: 0 0 7px;
      font-size: 0.98rem;
      color: #7a4d0b;
      letter-spacing: 0.01em;
    }
    .card p {
      margin: 0.15em 0;
      line-height: 1.47;
      font-size: 0.92rem;
    }
    ol {
      margin: 0.1em 0 0.2em 1.2em;
      padding: 0;
      font-size: 0.92rem;
      line-height: 1.46;
    }
    li { margin: 0.26em 0; }
    .full { margin-top: 10px; }
    .muted { color: #6b5b4a; font-style: italic; }
    @media print {
      @page { size: A4 portrait; margin: 10mm; }
      .page { max-width: 100%; padding: 0; }
      .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }
  </style>
</head>
<body>
  <main class="page">
    <section class="hero">
      <h1>${escapeHtml(name)} | Kampanya Detay Sunumu</h1>
      <p class="meta">
        <span class="chip">Şube: ${escapeHtml(branchName)}</span>
        <span class="chip">Dönem: ${escapeHtml(start)} - ${escapeHtml(end)}</span>
        <span class="chip">Çıktı: ${escapeHtml(new Date().toLocaleDateString('tr-TR'))}</span>
      </p>
    </section>

    <section class="grid">
      <article class="card">
        <h2>Kampanya Özeti</h2>
        <p>${escapeHtml(`Yönetici hedefi: ${campaignGoal}`)}</p>
      </article>
      <article class="card">
        <h2>AI Kampanya Hedefi</h2>
        <p>${escapeHtml(campaignAiGoal)}</p>
      </article>
      <article class="card">${printStoreSection}</article>
      <article class="card">${printStaffSection}</article>
      ${printCustomSection ? `<article class="card">${printCustomSection}</article>` : ''}
      ${printApprovedSection ? `<article class="card">${printApprovedSection}</article>` : ''}
    </section>

    <section class="card full">
      <h2>Şubenin Görevi</h2>
      <p>Bu kampanya döneminde şube, Görevler sekmesindeki onaylı kampanya maddelerini tamamladıkça işaretler. Yönetici panelindeki şube performansı bu işaretlemelere göre hesaplanır.</p>
    </section>
  </main>
</body>
</html>`;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      iframe.remove();
      return;
    }
    frameWindow.document.open();
    frameWindow.document.write(html);
    frameWindow.document.close();
    window.setTimeout(() => {
      frameWindow.focus();
      frameWindow.print();
      window.setTimeout(() => {
        iframe.remove();
      }, 800);
    }, 150);
  };

  if (!embedded && cloudBundle === null) {
    return (
      <Box sx={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8f4ea', p: 2, color: '#5c4a2e' }}>
        <Typography sx={{ fontWeight: 800 }}>Kampanya verileri yükleniyor…</Typography>
      </Box>
    );
  }

  return (
    <Box className="ky-staff-panel" sx={{ minHeight: embedded ? 'auto' : '100vh', bgcolor: '#f8f4ea', p: embedded ? 0 : 2, color: '#2f2516', overflowX: 'hidden' }}>
      <Box className="ky-staff-shell" sx={{ maxWidth: embedded ? '100%' : 980, mx: 'auto', border: embedded ? 0 : '1px solid rgba(151,113,39,0.22)', borderRadius: 2.4, p: embedded ? 0 : 1.6, background: embedded ? 'transparent' : 'linear-gradient(180deg, #fffaf0 0%, #f3eadb 100%)' }}>
        <Box className="ky-staff-header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ color: '#2f2516', fontWeight: 900, fontSize: embedded ? '1.32rem' : '1.5rem' }}>
              {branchName} Kampanya Paneli
            </Typography>
            <Typography sx={{ color: '#6b5b40', fontSize: '0.95rem' }}>
              Aktif kampanya: {safeString(campaign?.name, 'Kampanya seçilmedi')}
            </Typography>
          </Box>
          <Box sx={{ px: 1.4, py: 0.85, borderRadius: 1.5, border: '1px solid rgba(22,101,52,0.2)', bgcolor: 'rgba(34,197,94,0.08)' }}>
            <Typography sx={{ color: '#166534', fontSize: '1rem', fontWeight: 900 }}>Tamamlanma: %{completionRate}</Typography>
            <Typography sx={{ color: '#6b5b40', fontSize: '0.82rem' }}>{completedCount}/{tasks.length} kampanya görevi</Typography>
          </Box>
        </Box>

        <Box className="ky-staff-tabs-row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.1 }}>
          <Tabs
            className="ky-staff-tabs"
            value={tab}
            onChange={(_, nextTab) => {
              setTab(nextTab);
              setTaskPage(1);
            }}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 900, color: '#6b5b40' },
              '& .Mui-selected': { color: '#7a4d0b !important' },
              '& .MuiTabs-indicator': { bgcolor: '#9a6a1f' }
            }}
          >
            <Tab label="Kampanya Detayları" />
            <Tab label="Görevler" />
            <Tab label="Fikir Sun" />
            <Tab label="Kampanya görselleri" />
          </Tabs>
          {campaign && tab === 0 ? (
            <Button
              onClick={handlePrintCampaignDetail}
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 900,
                borderColor: 'rgba(151,113,39,0.42)',
                color: '#7a4d0b',
                bgcolor: 'rgba(255,255,255,0.62)',
                '&:hover': { borderColor: '#9a6a1f', bgcolor: 'rgba(154,106,31,0.08)' }
              }}
            >
              Yazdır
            </Button>
          ) : null}
        </Box>

        {!campaign ? (
          <Box sx={sectionSx}>
            <Typography sx={{ color: '#7a6b55', fontSize: '1rem' }}>Aktif kampanya yok. Yönetim panelinden bir kampanya seçin.</Typography>
          </Box>
        ) : tab === 0 ? (
          <Box sx={{ display: 'grid', gap: 1, maxHeight: embedded ? '62vh' : 'none', overflowY: embedded ? 'auto' : 'visible', pr: embedded ? 0.4 : 0 }}>
            <Box sx={sectionSx}>
              <Typography sx={detailTitleSx}>Kampanya Özeti</Typography>
              <Typography sx={{ ...detailTextSx, fontWeight: 900 }}>{safeString(campaign.name, 'Yeni Kampanya')}</Typography>
              <Typography sx={{ ...detailTextSx, color: '#5f5140', mt: 0.2 }}>
                Tarih: {safeString(campaign.start, '-')} - {safeString(campaign.end, '-')}
              </Typography>
              <Typography sx={{ ...detailTextSx, mt: 0.4 }}>Yönetici hedefi: {campaignGoal}</Typography>
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={detailTitleSx}>AI Kampanya Hedefi</Typography>
              <Typography sx={{ ...detailTextSx, whiteSpace: 'pre-wrap' }}>{campaignAiGoal}</Typography>
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={detailTitleSx}>Mağaza / Vitrin / Banko — İşaretlenen Fikirler</Typography>
              {renderDetailList(selectedStoreIdeas, 'Bu kanalda henüz işaretli fikir yok.')}
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={detailTitleSx}>Personel — İşaretlenen Fikirler</Typography>
              {renderDetailList(selectedStaffIdeas, 'Bu kanalda henüz işaretli fikir yok.')}
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={detailTitleSx}>Yeni Fikir (Sohbet ile Eklenenler)</Typography>
              {renderDetailList(selectedCustomIdeas, 'Henüz ek fikir yok.')}
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={detailTitleSx}>Şubelerden Onaylanan Fikirler</Typography>
              {renderDetailList(approvedSubmittedIdeas, 'Onaylanan şube fikri yok.')}
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={detailTitleSx}>Şubenin Görevi</Typography>
              <Typography sx={detailTextSx}>
                Bu kampanya döneminde şube, Görevler sekmesindeki onaylı kampanya maddelerini tamamladıkça işaretler. Yönetici panelindeki şube performansı bu işaretlemelere göre hesaplanır.
              </Typography>
            </Box>
          </Box>
        ) : tab === 1 ? (
          <Box sx={{ display: 'grid', gap: 0.8, maxHeight: embedded ? '62vh' : 'none', overflowY: embedded ? 'auto' : 'visible', pr: embedded ? 0.4 : 0 }}>
            {tasks.length ? pagedTasks.map((task, index) => (
              <Box key={task.id} sx={{ ...sectionSx, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.7, alignItems: 'start' }}>
                <Checkbox
                  checked={isChecked(task.id)}
                  onChange={() => toggleTask(task.id)}
                  sx={{ p: 0.25, color: '#8a7a63', '&.Mui-checked': { color: '#166534' } }}
                />
                <Box>
                  <Typography sx={{ color: '#7a4d0b', fontSize: '0.82rem', fontWeight: 900 }}>
                    {(currentPage - 1) * PAGE_SIZE + index + 1}. {task.group} Görevi
                  </Typography>
                  <Typography sx={{ color: '#2f2516', fontSize: '0.96rem', lineHeight: 1.38, mt: 0.2 }}>
                    {safeString(task.text, '-')}
                  </Typography>
                </Box>
              </Box>
            )) : (
              <Box sx={sectionSx}>
                <Typography sx={{ color: '#7a6b55', fontSize: '0.95rem' }}>
                  Bu kampanyada seçilmiş görev yok. Kampanya detayında Mağaza veya Personel sekmesinden görev seçip kaydedin.
                </Typography>
              </Box>
            )}
            {tasks.length > PAGE_SIZE ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.6, flexWrap: 'wrap', mt: 0.25 }}>
                {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                  <Button
                    key={page}
                    size="small"
                    variant={page === currentPage ? 'contained' : 'outlined'}
                    onClick={() => setTaskPage(page)}
                    sx={{
                      minWidth: 34,
                      textTransform: 'none',
                      fontWeight: 900,
                      color: page === currentPage ? '#fff8e1' : '#7a4d0b',
                      bgcolor: page === currentPage ? '#9a6a1f' : 'rgba(255,255,255,0.65)',
                      borderColor: 'rgba(151,113,39,0.32)',
                      '&:hover': { bgcolor: page === currentPage ? '#7a4d0b' : 'rgba(151,113,39,0.1)' }
                    }}
                  >
                    {page}
                  </Button>
                ))}
              </Box>
            ) : null}
          </Box>
        ) : tab === 2 ? (
          <Box sx={{ display: 'grid', gap: 1, maxHeight: embedded ? '62vh' : 'none', overflowY: embedded ? 'auto' : 'visible', pr: embedded ? 0.4 : 0 }}>
            <Box sx={sectionSx}>
              <Typography sx={{ color: '#7a4d0b', fontSize: '1rem', fontWeight: 900, mb: 0.45 }}>Kampanya İçin Fikir Sun</Typography>
              <Box
                sx={{
                  mb: 0.9,
                  borderRadius: 1.2,
                  border: isIdeaSubmitLocked ? '1px solid rgba(185,28,28,0.3)' : '1px solid rgba(22,101,52,0.3)',
                  bgcolor: isIdeaSubmitLocked ? 'rgba(254,226,226,0.45)' : 'rgba(220,252,231,0.5)',
                  px: 0.9,
                  py: 0.7
                }}
              >
                <Typography sx={{ color: isIdeaSubmitLocked ? '#991b1b' : '#166534', fontSize: '0.82rem', fontWeight: 900 }}>
                  Fikir sunma için kalan süre: {ideaCountdownText}
                </Typography>
                <Typography sx={{ color: isIdeaSubmitLocked ? '#991b1b' : '#166534', fontSize: '0.78rem', mt: 0.15 }}>
                  {isIdeaSubmitLocked
                    ? 'Kampanya başlangıcına son 24 saat kaldığı için fikir sunma kapandı.'
                    : 'Personel, kampanya başlangıcından 24 saat öncesine kadar fikir sunabilir.'}
                </Typography>
              </Box>
              <Typography sx={{ color: '#2f2516', fontSize: '0.92rem', lineHeight: 1.45, mb: 1 }}>
                Mağazada uygulanabilecek, müşteriye fayda sağlayacak veya satışı artıracak fikrinizi yazın. Yönetici uygun görürse kampanyaya dahil eder.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={ideaInput}
                disabled={isIdeaSubmitLocked}
                onChange={(event) => {
                  setIdeaInput(event.target.value);
                  setIdeaSuccess('');
                }}
                placeholder="Örn: 14 Ayar kolyeler için bankoda ayrı hediye tepsisi hazırlayalım..."
                sx={{
                  '& .MuiInputBase-root': { color: '#2f2516', bgcolor: 'rgba(255,255,255,0.82)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(151,113,39,0.24)' }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography sx={{ color: ideaSuccess ? '#166534' : '#7a6b55', fontSize: '0.88rem', fontWeight: 800 }}>
                  {ideaSuccess || 'Bir veya birden fazla fikir ayrı ayrı gönderilebilir.'}
                </Typography>
                <Button
                  variant="contained"
                  onClick={submitIdea}
                  disabled={isIdeaSubmitLocked || !safeString(ideaInput).trim()}
                  sx={{ textTransform: 'none', color: '#fff8e1', fontWeight: 900, bgcolor: '#9a6a1f', '&:hover': { bgcolor: '#7a4d0b' } }}
                >
                  Fikri Gönder
                </Button>
              </Box>
            </Box>
            <Box sx={sectionSx}>
              <Typography sx={{ color: '#7a4d0b', fontSize: '1rem', fontWeight: 900, mb: 0.45 }}>Gönderilen Fikirler</Typography>
              {safeArray(campaign?.submittedIdeas).filter((idea) => idea?.branchSlug === branchSlug).length ? (
                safeArray(campaign?.submittedIdeas).filter((idea) => idea?.branchSlug === branchSlug).map((idea) => (
                  <Typography
                    key={idea.id}
                    component="p"
                    sx={{ color: '#2f2516', fontSize: '0.92rem', lineHeight: 1.45, m: 0, mb: 0.55 }}
                  >
                    • {safeString(idea.text, '-')}
                    <IdeaTextCopyButton text={safeString(idea.text, '')} />
                    <Box component="span" sx={{ color: '#6b5b40', fontSize: '0.82rem', fontWeight: 700 }}>
                      {' '}
                      {idea.approved ? '(Kampanyaya dahil edildi)' : '(Yönetici onayı bekliyor)'}
                    </Box>
                  </Typography>
                ))
              ) : (
                <Typography sx={{ color: '#7a6b55', fontSize: '0.92rem' }}>Bu şube henüz fikir göndermedi.</Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 0.8, maxHeight: embedded ? '62vh' : 'none', overflowY: embedded ? 'auto' : 'visible', pr: embedded ? 0.4 : 0 }}>
            <Box sx={sectionSx}>
              <Typography sx={{ color: '#7a4d0b', fontSize: '1rem', fontWeight: 900, mb: 0.45 }}>Kampanya görselleri</Typography>
              <Typography sx={{ color: '#2f2516', fontSize: '0.9rem', lineHeight: 1.45, mb: 1 }}>
                Yönetim panelinde &quot;Görselleri yönet&quot; ile eklenen görselleri burada indirebilirsiniz. Görsel satırına yazılan &quot;ilgili fikir&quot; notu burada görünür. Şubede sadece indirme; yükleme yönetim panelindendir.
              </Typography>
              {VISUAL_SLOT_SECTIONS.some((_, secIdx) => getSectionSlotIndices(secIdx).some((slotIndex) => getSlotImage(campaign, slotIndex))) ? null : (
                <Typography sx={{ color: '#7a6b55', fontSize: '0.9rem', mb: 0.5 }}>
                  Henüz yüklenmiş görsel yok.
                </Typography>
              )}
              <Tabs
                value={visualSectionTab}
                onChange={(_, next) => setVisualSectionTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 0.9,
                  '& .MuiTab-root': { minHeight: 34, textTransform: 'none', fontWeight: 900, color: '#6b5b40', fontSize: '0.8rem', py: 0.35 },
                  '& .Mui-selected': { color: '#7a4d0b !important' },
                  '& .MuiTabs-indicator': { bgcolor: '#9a6a1f' }
                }}
              >
                {VISUAL_SLOT_SECTIONS.map((sec) => (
                  <Tab key={`staff-vis-tab-${sec.key}`} label={sec.label} />
                ))}
              </Tabs>
              <Box sx={{ display: 'grid', gap: 0.7 }}>
                {VISUAL_SLOT_SECTIONS.slice(visualSectionTab, visualSectionTab + 1).map((section) => {
                  const loadedSlotIndices = getSectionSlotIndices(visualSectionTab).filter((slotIndex) => !!getSlotImage(campaign, slotIndex));
                  if (!loadedSlotIndices.length) {
                    return (
                      <Typography key={`branch-vis-empty-${section.key}`} sx={{ color: '#7a6b55', fontSize: '0.88rem', fontStyle: 'italic' }}>
                        Bu alanda henüz görsel yüklenmedi.
                      </Typography>
                    );
                  }
                  return loadedSlotIndices.map((slotIndex, localIdx) => {
                    const dataUrl = getSlotImage(campaign, slotIndex);
                    return (
                      <Box
                        key={`branch-vis-${section.key}-${slotIndex}`}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '22px 120px 54px auto',
                          alignItems: 'start',
                          gap: 0.65,
                          p: 0.85,
                          borderRadius: 1.2,
                          border: '1px solid rgba(151,113,39,0.18)',
                          bgcolor: 'rgba(255,255,255,0.78)',
                          minWidth: 0
                        }}
                      >
                        <Typography sx={{ color: '#5f5140', fontSize: '0.88rem', fontWeight: 900, minWidth: 22 }}>{localIdx + 1}</Typography>
                        <Typography sx={{ color: '#7a4d0b', fontSize: '0.78rem', fontWeight: 900, whiteSpace: 'nowrap' }}>{section.label}</Typography>
                        {dataUrl ? (
                        <Tooltip
                          enterDelay={250}
                          placement="right"
                          title={
                            <Box>
                              <Typography component="p" variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 800, color: 'rgba(44,40,32,0.95)' }}>
                                Önizleme — Tıklayınca büyük
                              </Typography>
                              <Box component="img" src={dataUrl} alt="" sx={{ maxWidth: 260, maxHeight: 260, objectFit: 'contain', display: 'block', borderRadius: 1 }} />
                            </Box>
                          }
                          slotProps={{
                            popper: { sx: { zIndex: 1500 } },
                            tooltip: {
                              sx: {
                                p: 0.75,
                                maxWidth: 300,
                                bgcolor: 'rgba(255,250,240,0.98)',
                                border: '1px solid rgba(151,113,39,0.3)',
                                boxShadow: '0 10px 28px rgba(0,0,0,0.14)'
                              }
                            }
                          }}
                        >
                          <Box component="span" onClick={() => setImagePreview({ src: dataUrl, slot: localIdx + 1 })} sx={{ lineHeight: 0, cursor: 'pointer', display: 'inline-block' }}>
                            <Box
                              component="img"
                              src={dataUrl}
                              alt=""
                              sx={{
                                width: 52,
                                height: 52,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid rgba(151,113,39,0.25)',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': { transform: 'scale(1.1)', boxShadow: '0 4px 14px rgba(151,113,39,0.35)' }
                              }}
                            />
                          </Box>
                        </Tooltip>
                        ) : (
                          <Typography sx={{ color: '#8a785f', fontSize: '0.82rem' }}>Yok</Typography>
                        )}
                        {dataUrl ? (
                          <Tooltip title="İndir">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const ext = extFromDataUrl(dataUrl);
                                  downloadDataUrl(dataUrl, `kampanya-${campaignImageSlug || 'gorsel'}-${section.key}-${localIdx + 1}.${ext}`);
                                }}
                                sx={{ color: '#166534' }}
                                aria-label={`${section.label} görselini indir`}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ color: '#8a785f', fontSize: '0.82rem' }}>—</Typography>
                        )}
                      </Box>
                    );
                  });
                })}
              </Box>
            </Box>
          </Box>
        )}
        <Dialog
          open={!!imagePreview}
          onClose={() => setImagePreview(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { bgcolor: '#fffaf0', border: '1px solid rgba(151,113,39,0.2)', borderRadius: 2, position: 'relative', m: 2 }
          }}
        >
          <IconButton
            onClick={() => setImagePreview(null)}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1, color: '#3b2b14', bgcolor: 'rgba(255,255,255,0.9)', boxShadow: 1 }}
            aria-label="Kapat"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <DialogContent sx={{ pt: 3, px: 2, pb: 2 }}>
            {imagePreview ? (
              <>
                <Typography sx={{ color: '#5f5140', fontSize: '0.82rem', fontWeight: 800, mb: 0.75, pr: 4 }}>
                  Görsel {imagePreview.slot}
                </Typography>
                <Box
                  component="img"
                  src={imagePreview.src}
                  alt=""
                  sx={{
                    width: '100%',
                    maxHeight: 'min(78vh, 640px)',
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid rgba(151,113,39,0.2)',
                    bgcolor: 'rgba(255,255,255,0.5)',
                    display: 'block',
                    boxSizing: 'border-box'
                  }}
                />
              </>
            ) : null}
          </DialogContent>
        </Dialog>
      </Box>
      <style>{`
        @media (max-width: 600px) {
          .ky-staff-panel {
            padding: 0 !important;
          }

          .ky-staff-header {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          .ky-staff-header > .MuiBox-root:last-child {
            width: 100% !important;
          }

          .ky-staff-tabs-row {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          .ky-staff-tabs {
            width: 100% !important;
            max-width: 100% !important;
          }

          .ky-staff-tabs .MuiTab-root {
            min-width: 0 !important;
            font-size: 0.72rem !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
          }

          .ky-staff-tabs-row > .MuiButton-root {
            width: 100% !important;
          }

          .ky-staff-panel .MuiInputBase-input {
            font-size: 16px !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default StaffBranchCampaignPanel;
